import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Mail, ArrowLeft, ArrowRight, RotateCcw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { resetPassword, getApiEndpoint } from '@/utils/ERPUtils';
import site_logo from '@/assets/site_logo.jpg';
import { BRAND_NAME } from '@/utils/branding';
import SiteFooter from '@/components/common/SiteFooter';
import { logger } from '@/utils/logger';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [brandLogo, setBrandLogo] = useState<string | null>(null);
  const [brandName, setBrandName] = useState<string>(BRAND_NAME);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasNavigatedRef = React.useRef(false);

  // Redirect if already logged in (prevent double navigation)
  React.useEffect(() => {
    if (user && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Fetch company branding (logo + name) for pre-auth display - same
  // pattern as the login page. Falls back to the bundled site_logo.
  useEffect(() => {
    let mounted = true;
    axios
      .get(getApiEndpoint('GET_DEFAULT_BRANDING'))
      .then((res) => {
        if (!mounted) return;
        const data = res?.data?.message?.data;
        if (data?.logo_url) setBrandLogo(data.logo_url);
        if (data?.company_name) setBrandName(data.company_name);
      })
      .catch((err) => {
        logger.error('Branding fetch failed:', err);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    const { success, error } = await resetPassword(email.trim());
    setLoading(false);

    if (!success) {
      toast({
        title: "Reset Failed",
        description: error?.message || "Unable to send reset email. Please try again.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Check your email",
      description:
        "If an account exists for this email, you'll receive password reset instructions within a few minutes. Don't see it? Check your spam / junk folder, or contact support for more details",
      duration: 8000,
    });
    navigate("/"); // redirect back to login
  };

  return (
    <div className="dark min-h-screen flex flex-col bg-background text-foreground">
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr]">
        {/* LEFT - secure intelligence panel */}
        <section className="relative overflow-hidden hidden lg:flex flex-col items-center justify-center p-12 lg:p-16">
          {/* radial gradient overlays */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse at 20% 0%, hsl(var(--primary) / 0.10), transparent 55%), radial-gradient(ellipse at 80% 100%, hsl(var(--primary) / 0.08), transparent 50%)',
            }}
            aria-hidden="true"
          />

          <div className="relative z-10 max-w-[520px] text-center">
            <div className="mx-auto mb-10 flex h-20 w-20 items-center justify-center rounded-2xl border border-border bg-secondary/60">
              <RotateCcw className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-4xl lg:text-[44px] leading-[1.08] tracking-tight font-semibold mb-6">
              Secure Intelligence.
            </h1>
            <p className="text-[17px] leading-relaxed text-muted-foreground">
              Access your {brandName} workspace securely. We utilize
              enterprise-grade encryption to protect your knowledge assets.
            </p>
          </div>
        </section>

        {/* RIGHT - reset form panel */}
        <section className="relative flex flex-col p-8 sm:p-12 lg:p-14 lg:border-l border-border">
          {/* brand */}
          <header className="flex items-center gap-3">
            <img
              src={brandLogo || site_logo}
              alt={brandName}
              className="h-14 w-auto"
              // Dark-on-white logo -> white marks on the dark page (matches sidebar)
              style={{ filter: 'invert(1)', mixBlendMode: 'screen' }}
              onError={(e) => {
                // If the company-uploaded logo URL is broken, fall back to bundled default.
                (e.currentTarget as HTMLImageElement).src = site_logo;
              }}
            />
          </header>

          <div className="flex-1 flex items-center justify-center py-10">
            <div className="w-full max-w-md rounded-xl border border-border bg-card/40 p-8 sm:p-10">
              <h2 className="text-[28px] leading-tight tracking-tight font-semibold mb-2">
                Forgot password?
              </h2>
              <p className="text-[15px] text-muted-foreground mb-8">
                No worries, we'll send you reset instructions.
              </p>

              <form onSubmit={handleReset} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
                    <Input
                      id="email"
                      type="email"
                      maxLength={254}
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 relative h-11"
                      autoComplete="email"
                      required
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full h-12" disabled={loading}>
                  {loading ? (
                    "Sending..."
                  ) : (
                    <>
                      Send reset link
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>

              {/* Back to login */}
              <div className="mt-8 text-center">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-foreground/90 hover:text-primary transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Login
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>

      <SiteFooter />
    </div>
  );
};

export default ForgotPassword;
